import React, { useState, useEffect } from "react"
import { Table, Col, Row, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField } from "availity-reactstrap-validation"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { usePermissions } from "API/PermissionContext"

const VLCCRateChartStatusReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [allChillings, setAllChillings] = useState([])
  const [chillings, setChillings] = useState([])
  const [routes, setRoutes] = useState([])
  const [collections, setCollections] = useState([])
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [companies, setCompanies] = useState([])
  const [search, setSearch] = useState("")

  const today = new Date().toISOString().split("T")[0]

  useEffect(() => { loadMasterData() }, [])

  const loadMasterData = async () => {
    try {
      const [chResp, pResp, coResp] = await Promise.all([
        api.get("centers"), api.get("plants"), api.get("companies")
      ])
      const ccs = Array.isArray(chResp.data.data) ? chResp.data.data : chResp.data.data?.data || []
      setAllChillings(ccs)
      setChillings(ccs)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData)
      setPlants(pData)
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const showCompany = companies.length > 1

  const companyChange = async e => {
    const val = e.target.value
    setChillings(allChillings); setRoutes([]); setCollections([])
    if (val && val !== "") {
      try {
        const r = await api.get("plants-by-company/" + val)
        setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } catch (e) { console.error(e) }
    } else {
      setPlants(allPlants)
    }
  }

  const plantChange = async e => {
    const val = e.target.value
    setRoutes([]); setCollections([])
    if (val && val !== "") {
      try {
        const r = await api.get("chillings-by-plant/" + val)
        setChillings(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } catch (e) { console.error(e) }
    } else {
      setChillings(allChillings)
    }
  }

  const ccChange = async e => {
    try {
      const val = e.target.value
      const u = !val || val === "0" ? "routes" : "route-cc/" + val
      const r = await api.get(u)
      setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      setCollections([])
    } catch (e) { console.error(e) }
  }

  const routeChange = async e => {
    try {
      const val = e.target.value
      if (val && val !== "0") {
        const r = await api.get("collection-center-route/" + val)
        setCollections(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } else {
        const r = await api.get("collection-centers")
        setCollections(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }
    } catch (e) { console.error(e) }
  }

  const handleSubmit = async (event, values) => {
    setLoading(true)
    try {
      const resp = await api.post("reports/vlcc-ratechart-status", values)
      if (resp.data.success) {
        setResults(resp.data.data || [])
      } else {
        setResults([])
      }
      setSubmitted(true)
    } catch (error) {
      console.error(error)
      setResults([])
      setSubmitted(true)
    }
    setLoading(false)
  }

  const statusLabel = s => {
    switch (parseInt(s)) {
      case 1: return "Approval Pending"
      case 2: return "App Download Pending"
      case 3: return "Downloaded in App"
      default: return "Unknown"
    }
  }

  const statusBadge = s => {
    switch (parseInt(s)) {
      case 1: return "bg-warning text-dark"
      case 2: return "bg-info"
      case 3: return "bg-success"
      default: return "bg-secondary"
    }
  }

  const milkTypeLabel = v => {
    switch (String(v)) {
      case "1": return "Cow"
      case "2": return "Buffalo"
      case "3": return "Mixed"
      default: return v || "-"
    }
  }

  const calcTypeLabel = v => {
    switch (String(v)) {
      case "1": return "FAT Based"
      case "2": return "FAT & SNF Based"
      case "3": return "Flat Rate"
      default: return v || "-"
    }
  }

  const downloadPdf = () => {
    const pdf = new jsPDF("l")
    pdf.setFontSize(14)
    pdf.text("VLCC Rate Chart Status Report", 14, 15)
    pdf.autoTable({ html: "#rcStatusTbl", startY: 22, styles: { fontSize: 6 } })
    pdf.save("VLCC-RateChart-Status-Report.pdf")
  }

  const downloadCsv = () => {
    if (!results.length) return
    let csv = "S.No," + (showCompany ? "Company," : "") + "Plant,Chilling Center,Route,Center No,Center,Rate Chart,Milk Type,Calculation,Effective Date,Status,Approved At,Synced At,Created At\n"
    results.forEach((r, i) => {
      csv += `${i + 1},`
      if (showCompany) csv += `"${r.companyName || ''}",`
      csv += `"${r.plantName || ''}","${r.chillingCenterName || ''}","${r.routeName || ''}","${r.centerNo || ''}","${r.centerName || ''}","${r.ratechartName || ''}","${milkTypeLabel(r.milkType)}","${calcTypeLabel(r.calculationType)}","${r.effectiveDate || ''}","${statusLabel(r.status)}","${r.approved_at || ''}","${r.synced_at || ''}","${r.created_at || ''}"\n`
    })
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = "VLCC-RateChart-Status-Report.csv"
    link.click()
  }

  const filtered = search
    ? results.filter(r =>
        (r.companyName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.plantName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.chillingCenterName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.routeName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.centerName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.ratechartName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.centerNo || "").toString().includes(search) ||
        statusLabel(r.status).toLowerCase().includes(search.toLowerCase())
      )
    : results

  // Summary counts
  const pendingCount = results.filter(r => parseInt(r.status) === 1).length
  const downloadPendingCount = results.filter(r => parseInt(r.status) === 2).length
  const downloadedCount = results.filter(r => parseInt(r.status) === 3).length

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint"><Row><Col md={12}><h4>VLCC Rate Chart Status Report</h4></Col></Row></div>
      <div className="dashContent">
        <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
          {showCompany && <Row>
            <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
          </Row>}
          <Row>
            <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All</option>{chillings.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route" onChange={routeChange}><option value="">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="collectionCenter" label="Center"><option value="">All</option>{collections.map((c, i) => <option key={i} value={c.id}>{c.centerName}</option>)}</AvField></div></Col>
          </Row>
          <Row>
            <Col md={3}><div className="mb-3"><AvField name="effectiveDate" label="Effective Date *" type="date" value={today} required /></div></Col>
            <Col md={3}>
              <div className="mb-3">
                <AvField type="select" name="status" label="Status">
                  <option value="">All</option>
                  <option value="1">Approval Pending</option>
                  <option value="2">App Download Pending</option>
                  <option value="3">Downloaded in App</option>
                </AvField>
              </div>
            </Col>
            <Col md={3} className="d-flex align-items-end pb-3">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner animation="border" size="sm" /><span style={{ paddingLeft: "10px" }}>Submitting...</span></> : <><i className="fas fa-check me-1"></i>Submit</>}
              </button>
            </Col>
          </Row>
        </AvForm>

        {submitted && <>
          {/* Status Summary Cards */}
          <Row className="mb-3 notToPrint">
            <Col md={3}>
              <div className="card mini-stats-wid mb-0">
                <div className="card-body" style={{ padding: "12px 16px" }}>
                  <div className="d-flex"><div className="flex-grow-1"><p className="text-muted fw-medium mb-1">Total Records</p><h5 className="mb-0">{results.length}</h5></div><div className="mini-stat-icon avatar-sm rounded-circle bg-primary align-self-center"><span className="avatar-title"><i className="fas fa-list-alt font-size-20"></i></span></div></div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="card mini-stats-wid mb-0">
                <div className="card-body" style={{ padding: "12px 16px" }}>
                  <div className="d-flex"><div className="flex-grow-1"><p className="text-muted fw-medium mb-1">Approval Pending</p><h5 className="mb-0 text-warning">{pendingCount}</h5></div><div className="mini-stat-icon avatar-sm rounded-circle bg-warning align-self-center"><span className="avatar-title"><i className="fas fa-clock font-size-20"></i></span></div></div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="card mini-stats-wid mb-0">
                <div className="card-body" style={{ padding: "12px 16px" }}>
                  <div className="d-flex"><div className="flex-grow-1"><p className="text-muted fw-medium mb-1">Download Pending</p><h5 className="mb-0 text-info">{downloadPendingCount}</h5></div><div className="mini-stat-icon avatar-sm rounded-circle bg-info align-self-center"><span className="avatar-title"><i className="fas fa-download font-size-20"></i></span></div></div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="card mini-stats-wid mb-0">
                <div className="card-body" style={{ padding: "12px 16px" }}>
                  <div className="d-flex"><div className="flex-grow-1"><p className="text-muted fw-medium mb-1">Downloaded</p><h5 className="mb-0 text-success">{downloadedCount}</h5></div><div className="mini-stat-icon avatar-sm rounded-circle bg-success align-self-center"><span className="avatar-title"><i className="fas fa-check-circle font-size-20"></i></span></div></div>
                </div>
              </div>
            </Col>
          </Row>

          {canExport(MID) && <div className="exportBtns notToPrint mb-2">
            <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
            <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
            <button className="btn btn-info btn-sm m-1" onClick={downloadCsv}><i className="fas fa-file-csv me-1"></i>CSV</button>
          </div>}

          <Row className="mb-2 notToPrint">
            <Col md={9}><span className="text-muted">Showing {filtered.length} of {results.length} entries</span></Col>
            <Col md={3}><input type="text" className="form-control form-control-sm" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} /></Col>
          </Row>

          <div className="table-responsive">
            <Table id="rcStatusTbl" className="dairyTbl" size="sm" bordered>
              <thead>
                <tr>
                  <th>#</th>
                  {showCompany && <th>Company</th>}
                  <th>Plant</th><th>Chilling Center</th><th>Route</th>
                  <th>Center No</th><th>Center</th><th>Rate Chart</th>
                  <th>Milk Type</th><th>Calculation</th>
                  <th>Effective Date</th><th>Status</th>
                  <th>Approved At</th><th>Synced At</th><th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? filtered.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    {showCompany && <td>{r.companyName || '-'}</td>}
                    <td>{r.plantName || '-'}</td>
                    <td>{r.chillingCenterName || '-'}</td>
                    <td>{r.routeName || '-'}</td>
                    <td>{r.centerNo || '-'}</td>
                    <td>{r.centerName || '-'}</td>
                    <td>{r.ratechartName || '-'}</td>
                    <td>{milkTypeLabel(r.milkType)}</td>
                    <td>{calcTypeLabel(r.calculationType)}</td>
                    <td>{r.effectiveDate || '-'}</td>
                    <td><span className={`badge ${statusBadge(r.status)}`}>{statusLabel(r.status)}</span></td>
                    <td>{r.approved_at ? new Date(r.approved_at).toLocaleDateString("en-IN") : '-'}</td>
                    <td>{r.synced_at ? new Date(r.synced_at).toLocaleDateString("en-IN") : '-'}</td>
                    <td>{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : '-'}</td>
                  </tr>
                )) : <tr><td colSpan={showCompany ? 15 : 14} className="text-center py-3">No Records Found</td></tr>}
              </tbody>
              {filtered.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}><td colSpan={showCompany ? 15 : 14}>Showing {filtered.length} of {results.length} entries</td></tr></tfoot>}
            </Table>
          </div>
        </>}
      </div>
    </div>
  )
}

export default VLCCRateChartStatusReport
